/**
 * @fileoverview Modal — shared portal-mounted modal primitive.
 * @package @puffin-core/page-shell
 *
 * Factory-first replacement for ad-hoc `<div className="modal-overlay">…<div
 * className="modal">…` consumers. Mounts via `React.createPortal` so the
 * `position: fixed` overlay always covers the viewport regardless of any
 * ancestor's `transform` / `filter` / `contain` (which would otherwise
 * promote the ancestor into the containing block for fixed descendants
 * and silently break the backdrop).
 *
 * Renders only canonical CSS classes from
 * `@puffin-core/shared-styles/css/utilities/modals.css`:
 *   .modal-overlay  — fixed backdrop (rgba(0,0,0,.5))
 *   .modal          — content box (padding owned by the class — see
 *                     modals.css "factory-first remediation, 2026-05-10")
 *   .modal-header   — title row
 *   .modal-title    — h2 title
 *   .modal-body     — body wrapper
 *   .modal-footer   — bordered footer for actions
 *
 * Use `<ConfirmModal>` for the confirm-or-cancel pattern (most common case).
 *
 * ## Usage
 *
 *     const [isOpen, setIsOpen] = useState(false);
 *     <Modal
 *       isOpen={isOpen}
 *       onClose={() => setIsOpen(false)}
 *       title="Discard changes?"
 *       footer={
 *         <>
 *           <button className="btn btn-secondary" onClick={cancel}>Cancel</button>
 *           <button className="btn btn-primary" onClick={confirm}>Discard</button>
 *         </>
 *       }
 *     >
 *       <p>Your unsaved edits will be lost.</p>
 *     </Modal>
 *
 * @module @puffin-core/page-shell/Modal
 */

import { memo, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { X as CloseIcon } from 'lucide-react';

import { useShellTranslation } from '../hooks/useShellTranslation.js';
import { useModalChrome, DialogScrim, DISMISSAL_CLOSE_BUTTON } from '@puffin-core/dialog-chrome';

/**
 * Size → CSS class mapping. Resolves to a class from the size shorthand
 * family in `@puffin-core/shared-styles/css/utilities/modals.css`:
 *
 *     sm  → .modal-sm   (400px)   tight confirmation dialogs
 *     md  → ''          (500px)   default — applies on `.modal` alone
 *     lg  → .modal-lg   (800px)   form modals (Create Function/Skill, etc.)
 *     xl  → .modal-xl   (1000px)  data-heavy modals (audit history, etc.)
 *
 * The `.modal.modal-{sm,lg,xl}` rules were added 2026-05-10 alongside
 * the Create Function modal-width fix — before then, only `.modal.modal-md`
 * was supported, so `size="lg"` silently fell back to the 500px default.
 */
const SIZE_CLASSES = Object.freeze({
  sm: 'modal-sm',
  md: '',
  lg: 'modal-lg',
  xl: 'modal-xl',
});

/**
 * The `.modal-header` row — the title plus the optional top-right close X.
 * Returns `null` when there is no title (the close button lives in the header
 * row, so it cannot render without one). Module-private and deliberately
 * UNEXPORTED: `web/single-component-export` allows only one exported component
 * per file, and this is an implementation detail of `<Modal>`.
 */
function ModalHeader({
  title,
  titleId,
  showCloseButton,
  onClose,
  closeLabel,
  closeButtonTestId,
  closeDisabled,
}) {
  if (!title) return null;

  return (
    <div className="modal-header">
      <h2 id={titleId} className="modal-title">
        {title}
      </h2>
      {showCloseButton ? (
        <button
          type="button"
          className="modal-close"
          // 🔴 `onClick={onClose}` handed the consumer React's synthetic MouseEvent as the
          // first argument. Now that the chrome reports a dismissal SOURCE, a consumer reading
          // that first parameter would have received a string from Escape and the backdrop and
          // an EVENT from this button. It names itself instead.
          onClick={() => onClose(DISMISSAL_CLOSE_BUTTON)}
          // A dialog that refuses to close while work is in flight must SAY so on the control.
          // Leaving the X enabled and no-opping its handler renders a button that takes focus in
          // the Tab cycle and does nothing — `disabled` is the only state assistive technology
          // announces (MODALCHROME-BURNDOWN-001 L6).
          disabled={closeDisabled}
          aria-label={closeLabel}
          data-testid={closeButtonTestId}
        >
          <CloseIcon size={18} aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

ModalHeader.propTypes = {
  title: PropTypes.node,
  titleId: PropTypes.string.isRequired,
  showCloseButton: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  closeLabel: PropTypes.string,
  closeButtonTestId: PropTypes.string,
  closeDisabled: PropTypes.bool,
};

/**
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose                Required — fires on ESC or backdrop click
 * @param {React.ReactNode} [props.title]         Renders a `.modal-header` + `.modal-title`
 * @param {React.ReactNode} [props.children]      Body content
 * @param {React.ReactNode} [props.footer]        Right-aligned footer actions
 * @param {'sm'|'md'|'lg'|'xl'} [props.size='md']
 * @param {boolean} [props.closeOnBackdrop=true]
 * @param {boolean} [props.closeOnEscape=true]
 * @param {React.RefObject} [props.initialFocusRef] Element to focus when opened.
 *                                                 If omitted, the dialog itself
 *                                                 receives focus (for screen-reader
 *                                                 announcement).
 * @param {string} [props.className]              Extra classes on the `.modal` box
 * @param {string} [props.bodyClassName]          Extra classes on the `.modal-body` wrapper.
 *                                                `<EditDrawer>` has always taken this; `<Modal>`
 *                                                did not, and the asymmetry mattered: this
 *                                                component ALWAYS wraps `children`, so a surface
 *                                                whose own box class lays its children out
 *                                                directly (a flex column, a grid) loses that
 *                                                layout to the wrapper and cannot compose the
 *                                                primitive at all.
 * @param {string} [props.testId='modal']
 * @param {string} [props.ariaLabelledBy]         Override auto-generated title id
 * @param {string} [props.ariaLabel]              Accessible name for a dialog with NO visible
 *                                                title. A dialog must have one name or the other;
 *                                                `<AlertDialog>` has always taken this and
 *                                                `<Modal>` did not, which is one of the seams that
 *                                                kept titleless surfaces (re-auth's step-up
 *                                                overlay, login-shell's subdomain lookup)
 *                                                hand-rolling their own markup.
 * @param {string} [props.ariaDescribedBy]
 * @param {boolean} [props.showCloseButton=true]  Render a top-right close X
 *                                                 inside the header. Only mounted
 *                                                 when `title` is also supplied
 *                                                 (the close button lives in the
 *                                                 modal-header row). Pass `false`
 *                                                 to suppress it (e.g.
 *                                                 force-acknowledge modals).
 * @param {string} [props.closeLabel='Close']
 * @param {string} [props.closeButtonTestId]      e.g. 'my-modal-close'
 * @param {boolean} [props.closeDisabled=false]   Disables the close X. For a dialog that is
 *                                                TEMPORARILY undismissable because work is in
 *                                                flight — pair it with `closeOnEscape={false}`
 *                                                and `closeOnBackdrop={false}` so all three
 *                                                exits agree. Distinct from `showCloseButton`,
 *                                                which is the surface's standing posture: a
 *                                                button that vanishes mid-operation reflows the
 *                                                header, and one that is merely inert lies.
 * @returns {React.JSX.Element|null}
 */
function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEscape = true,
  initialFocusRef,
  onOpen,
  className = '',
  bodyClassName = '',
  role = 'dialog',
  testId = 'modal',
  ariaLabelledBy,
  ariaLabel,
  ariaDescribedBy,
  showCloseButton = true,
  closeLabel,
  closeButtonTestId,
  closeDisabled = false,
}) {
  const modalClasses = useMemo(() => {
    const parts = ['modal'];
    const sizeClass = SIZE_CLASSES[size];
    if (sizeClass) parts.push(sizeClass);
    if (className) parts.push(className);
    return parts.join(' ');
  }, [size, className]);

  // Stop click bubbling out of the modal box so backdrop-click only fires when
  // the user actually clicks outside the dialog.
  const handleDialogClick = useCallback((event) => {
    event.stopPropagation();
  }, []);

  // MODALCHROME-001 (owner ruling R-9, 2026-09-18): the ONE modal chrome — scroll lock, Escape,
  // focus capture/restore, the backdrop handler, the `aria-labelledby` id, and the **Tab cycle this
  // component never had**. Every `<Modal>` in the fleet could be tabbed straight out of before it.
  // MODALCHROME-BURNDOWN-001 L1 (2026-09-20): `onOpen` is PASSED THROUGH.
  //
  // `useModalChrome` has always taken it and this wrapper silently dropped it — so a dialog that
  // needs to reset its own state as it opens could not use `<Modal>` at all and had to call the
  // hook directly and hand-roll the markup. That is why login-shell's three modals were on the
  // hook with their own `modal-overlay`: not stubbornness, a missing seam.
  //
  // ⚠️ A sibling prop `focusTrapDeps` was added here in the same pass and then PRUNED, because
  // measurement showed it was inert: the Tab cycle re-queries the focusable set on every
  // keystroke, so a dialog whose content changes already cycles correctly with no deps at all.
  // What it was reaching for — a cycle that binds when the container appears late — the hook now
  // gets by construction from a state-backed callback ref. Do not reintroduce it.
  const { containerRef, titleId, handleBackdropClick } = useModalChrome({
    isOpen,
    onClose,
    onOpen,
    closeOnEscape,
    closeOnBackdrop,
    initialFocusRef,
  });
  const t = useShellTranslation();

  if (!isOpen) return null;
  // `@puffin-core/page-shell` is browser-only (package.json exports only the
  // `browser` condition), so `document.body` is always present for createPortal.

  const labelledBy = ariaLabelledBy || (title ? titleId : undefined);
  // `aria-label` ONLY when nothing else names the dialog. An element carrying both is not a
  // fallback — every AT ignores the label when `aria-labelledby` resolves — so emitting both would
  // put a claim in the markup that nothing reads, and let the two drift apart unnoticed.
  const fallbackLabel = labelledBy ? undefined : ariaLabel;

  return createPortal(
    // `<DialogScrim>` rather than a local `<div className="modal-overlay">` (Q-L2-4, 2026-09-20).
    // The class now has exactly ONE renderer in the repo, in `@puffin-core/dialog-chrome`. This
    // file used to be that renderer, which is why `no-local-modal-chrome` exempts it BY PATH — and
    // that exemption was the whole problem: `location-picker` needs the same scrim and cannot
    // import `<Modal>` without a dependency cycle, so the rule's only instruction was impossible
    // for it. A leaf every package can reach removes the need to grow the exemption list.
    <DialogScrim onBackdropClick={handleBackdropClick} testId={testId}>
      <div
        ref={containerRef}
        className={modalClasses}
        // 🔴 `alertdialog` for the interrupting registers (Q-L2-3, owner ruling 2026-09-20).
        // WAI-ARIA APG lists "action confirmation prompts" under `alertdialog`, and every
        // destructive confirm in the fleet was announcing itself as a plain `dialog`.
        // `<ConfirmModal>` drives this from its `variant`, so ONE decision corrects all of them
        // rather than twenty per-file judgements.
        role={role}
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={fallbackLabel}
        aria-describedby={ariaDescribedBy}
        tabIndex={-1}
        onClick={handleDialogClick}
      >
        <ModalHeader
          title={title}
          titleId={titleId}
          showCloseButton={showCloseButton}
          onClose={onClose}
          closeLabel={closeLabel ?? t('common.close', { defaultValue: 'Close' })}
          closeButtonTestId={closeButtonTestId}
          closeDisabled={closeDisabled}
        />
        <div className={bodyClassName ? `modal-body ${bodyClassName}` : 'modal-body'}>
          {children}
        </div>
        {footer ? <div className="modal-footer">{footer}</div> : null}
      </div>
    </DialogScrim>,
    document.body,
  );
}

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  children: PropTypes.node,
  footer: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  closeOnBackdrop: PropTypes.bool,
  closeOnEscape: PropTypes.bool,
  // PropTypes can't strongly type a ref; a plain object with a `current`
  // mutable slot is the conventional shape.
  initialFocusRef: PropTypes.shape({
    current: PropTypes.any,
  }),
  /** Stable (`useCallback`) hook fired as the dialog opens, BEFORE the scroll lock and focus capture. */
  onOpen: PropTypes.func,
  className: PropTypes.string,
  /** Extra classes on the `.modal-body` wrapper — mirrors `<EditDrawer>`'s prop of the same name. */
  bodyClassName: PropTypes.string,
  role: PropTypes.oneOf(['dialog', 'alertdialog']),
  testId: PropTypes.string,
  ariaLabelledBy: PropTypes.string,
  /** Accessible name for a dialog with no visible title. Ignored when a title or labelledBy wins. */
  ariaLabel: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
  showCloseButton: PropTypes.bool,
  closeLabel: PropTypes.string,
  closeButtonTestId: PropTypes.string,
  /** Disables the close X while the dialog is temporarily undismissable (work in flight). */
  closeDisabled: PropTypes.bool,
};

const MemoizedModal = memo(Modal);
export { MemoizedModal as Modal };
